import ButtonOptions from '@/components/ButtonOptions';
import PageContainer from '@/components/PageContainer';
import useFirequery from '@/hooks/useFirequery';
import IconText from '@/components/IconText';
import TestCard from '@/components/TestCard';

import { getLabelColor, getLabelStatus } from '@/utils/helpers';
import { patientCrud, testCrud } from '@/services/firebase';
import { Box, Card, Chip, Typography } from '@mui/material';
import { EntityList } from '@/components/EntityManagement';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Patient, Test } from '@/types/database';
import { sendReminder } from '@/services/api';
import { useSnackbar } from 'notistack';
import { FC, useEffect } from 'react';
import { useAuth } from '@/components/auth';

const PatientDetailPage: FC = () => {
  const { id } = useParams<{ clinicId: string; id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user, getToken } = useAuth();

  const {
    data: patient,
    loading: loadingPatient,
    error: errorPatient,
  } = useFirequery<Patient>(patientCrud.query(id as string));
  const clinicId = patient?.clinicId;
  const {
    data: tests,
    loading: loadingTests,
  } = useFirequery<Test[]>(testCrud.query({ where: [['patientId', '==', id]], orderBy: [['createdAt', 'desc']] }), {
    disabled: !id,
  });

  useEffect(() => {
    if (!loadingPatient && !patient) {
      navigate(`/clinics/${patient?.clinicId}/patients`);
      enqueueSnackbar('Patient not found', { variant: 'error' });
    }
  }, [patient, loadingPatient, clinicId]);

  const onClickExam = (item) => {
    navigate(`./tests/${item.id}`);
  };

  const onClickOption = (option) => {
    let promise: Promise<any>;
    switch (option.value) {
      case 'send-reminder':
        promise = getToken().then(token => sendReminder(patient as Patient, tests?.[0], token));
        break;
      case 'archive':
        promise = patientCrud.update(id as string, { status: 'archived' });
        break;
      case 'unarchive':
        promise = patientCrud.update(id as string, { status: 'active' });
        break;
      default:
        promise = Promise.resolve();
    }

    return promise.then(() => {
      enqueueSnackbar('Reminder sent successfully', { variant: 'success' });
    }).catch((error) => {
      enqueueSnackbar(`Error: ${error.message || error}`, { variant: 'error' });
    });
  };

  const options = [
    { label: 'Send reminder', value: 'send-reminder' },
    patient?.status === 'archived'
      ? { label: 'Unarchive', value: 'unarchive' }
      : { label: 'Archive', value: 'archive' },
  ];

  return (
    <PageContainer loading={loadingPatient} error={errorPatient?.message} goback title="patientDetailsTitle">
      <Card square sx={{ px: 1, py: 2, mt: 1 }}>
        <ButtonOptions
          sx={{ position: 'absolute', top: 40, right: 30, zIndex: 1 }}
          icon="iwwa:option"
          onClick={onClickOption}
          options={options}
        />
        <Box display="flex" alignItems="center" gap={2} position="relative">
          <Box textAlign="center">
            <Icon height={64} style={{ display: 'block', margin: '0 auto' }} icon="fluent:patient-32-filled" />
            {getLabelStatus(patient?.status) && (
              <Chip color={getLabelColor(patient?.status)} size="small" label={getLabelStatus(patient?.status)} />
            )}
          </Box>
          <Box>
            <Typography variant="h2">{patient?.name}</Typography>
            {patient?.email && (
              <IconText variant="body2" icon="ic:outline-email">
                {patient?.email}
              </IconText>
            )}
            {patient?.phone && (
              <IconText variant="body2" icon="ic:baseline-phone">
                {patient?.phone}
              </IconText>
            )}
          </Box>
        </Box>
        <Typography mt={2} variant="body1">
          {patient?.description}
        </Typography>
      </Card>

      <EntityList
        loading={loadingTests}
        onClick={user.role === 'doctor' ? onClickExam : undefined}
        mt={3}
        title="Exams"
        disableCreate
        item={TestCard}
        items={tests}
        gap={1}
      />
    </PageContainer>
  );
};

export default PatientDetailPage;
